import React, { useState } from 'react';
import { Box, Button, Paper, TextField, Typography, InputAdornment, Divider } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LoginIcon from '@mui/icons-material/Login';
import { Navigate, useLocation, useNavigate, Location } from 'react-router-dom';
import { login } from '../services/auth';
import { useSnackbar } from './SnackbarContext';

interface LocationState {
  from?: Location;
}

export default function AdminLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const nav = useNavigate();
  const location = useLocation() as Location & { state?: LocationState };
  const { showSnackbar } = useSnackbar();

  const token = localStorage.getItem('authToken');
  if (token) {
    return <Navigate to="/admin" replace />;
  }

  const from = location.state?.from?.pathname ?? '/admin';

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const jwt = await login(username, password);
      localStorage.setItem('authToken', jwt);
      showSnackbar('Prihlásenie úspešné', 'success');
      nav(from, { replace: true });
    } catch {
      showSnackbar('Nesprávne meno alebo heslo', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = username.trim().length > 0 && password.length > 0 && !submitting;

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: '100%',
          maxWidth: 360,
          p: 2.5,
          borderRadius: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LockOutlinedIcon fontSize="small" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Administrácia
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ opacity: 0.8, mb: 1.5 }}>
          Prihláste sa pre prístup do admin sekcie
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Box
          component="form"
          onSubmit={onSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
        >
          <TextField
            size="small"
            label="Užívateľské meno"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            size="small"
            label="Heslo"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
            startIcon={<LoginIcon />}
            sx={{ mt: 0.5, borderRadius: 2 }}
          >
            {submitting ? 'Prihlasujem…' : 'Prihlásiť'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
